import { useCallback, useMemo } from 'react';
import { makeStyles, tokens, Text, Spinner, TabList, Tab } from '@coreai/fluentui-react';
import { ShieldPersonRegular, OptionsRegular, GiftRegular } from '@fluentui/react-icons';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAppsInProject } from '@/hooks/useApps';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { PageSection } from '@/components/shared/PageSection';
import { ProjectOverviewHeader } from '@/components/shared/ProjectOverviewHeader';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { ApiError } from '@/services/apiError';
import { Permissions } from '@/components/SettingsDialog/Permissions/Permissions';
import { ObservabilityTab } from '@/components/Settings/ObservabilityTab';
import { FreeGrantBalances } from '@/components/Project/FreeGrantBalances';
import { useCheckAccess, checkUserAccess, useIsProjectOwner } from '@/hooks/useUserPermissions';
import { useAuth } from '@/auth';
import { PermissionType, PROJECT_PERMISSIONS } from '@/types/permissions';
import { getIsFreeProject } from '@/types/automation';
import { SETTINGS_TAB, SETTINGS_TAB_PARAM } from '@/routes/queryParams';

const useStyles = makeStyles({
  page: {
    // Outer padding + horizontal layout come from `ProjectLayout.mainContent`.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  tabList: {
    // Pull the underline to the full width of the section so the active-tab
    // indicator reads as a divider between the tab strip and the panel below.
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  panel: {
    paddingTop: tokens.spacingVerticalL,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
  },
});

export const ProjectSettingsPage = () => {
  const styles = useStyles();
  const { projectName, project, isLoading, notFound } = useCurrentProject();
  const { data: apps } = useAppsInProject(projectName ?? '');
  useDocumentTitle(['Settings', projectName]);
  const { account, getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: myAccess, isLoading: accessLoading, isError } = useCheckAccess(projectName ?? '', undefined, null);
  const { data: isUserProjectAdmin, isLoading: isAdminAccessLoading } = useIsProjectOwner(projectName ?? '');
  const userCanEdit = useMemo(() => isUserProjectAdmin || myAccess?.role === 'Contributor', [isUserProjectAdmin, myAccess]);

  const isFreeProject = getIsFreeProject(project);
  // The free-grants surface (tab, panel, and its balances query) is gated behind
  // a portal feature flag layered on top of `isFreeProject`, so the tab, banner,
  // and badge roll back / region-gate together.
  const freeGrantsEnabled = useFeatureFlag('freeGrants');
  const showFreeGrants = freeGrantsEnabled && isFreeProject;

  // The active tab is deep-linkable via `?tab=<tab>` so surfaces like the
  // free-grant banner can jump straight to a specific tab. Derive the selection
  // from the current query parameter each render (single source of truth) so a
  // same-route navigation or browser history change keeps the panel in sync —
  // rather than snapshotting `?tab=` once on mount. Unknown tabs (and the Free
  // grants tab when it's unavailable, e.g. a non-free project or the flag is
  // off via a stale/shared link) fall back to the default permissions tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get(SETTINGS_TAB_PARAM);
  const selectedTab =
    requestedTab === SETTINGS_TAB.observability
      ? SETTINGS_TAB.observability
      : requestedTab === SETTINGS_TAB.freeGrants && showFreeGrants
        ? SETTINGS_TAB.freeGrants
        : SETTINGS_TAB.permissions;

  const handleTabSelect = useCallback(
    (value: string) => {
      // Reflect the active tab in the URL (preserving any other params) so the
      // selection is shareable and back/forward-friendly; the derived
      // `selectedTab` above then reacts to the change.
      const next = new URLSearchParams(searchParams);
      next.set(SETTINGS_TAB_PARAM, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const currentUserAccess = useMemo(() => {
    if (accessLoading || isAdminAccessLoading) return null;
    return isUserProjectAdmin
      ? { role: PermissionType.Owner, permissions: [{
          id: PermissionType.Owner,
          name: 'Owner',
          description: 'Full access to all environment resources and permissions. Cannot manage application resources unless explicitly granted at the application level, although they can still delete the application.',
      }] }
      : myAccess;
  }, [accessLoading, isAdminAccessLoading, isUserProjectAdmin, myAccess]);

  const resourceDetails = useMemo(() => ({ projectName: projectName ?? '' }), [projectName]);

  const checkAccess = useCallback(async (userId: string) => {
    try {
      return await checkUserAccess(queryClient, account?.tenantId ?? '', userId, projectName ?? '', undefined, getOttoToken);
    } catch (error) {
      console.error('Error checking access for user', userId, error);
      throw error;
    }
  }, [account?.tenantId, getOttoToken, projectName, queryClient]);

  if (isLoading) {
    return <Spinner size="medium" label="Loading environment settings..." />;
  }

  if (notFound) {
    return <ResourceErrorPage resourceType="project" error={new ApiError('Not found', 404)} />;
  }

  return (
    <div className={styles.page}>
      {projectName && (
        <ProjectOverviewHeader
          name={projectName}
          project={project}
          appCount={apps?.length ?? 0}
        />
      )}

      <PageSection title="Settings">
        <TabList
          className={styles.tabList}
          selectedValue={selectedTab}
          onTabSelect={(_, data) => handleTabSelect(data.value as string)}
        >
          <Tab value="permissions" icon={<ShieldPersonRegular />}>
            User permissions
          </Tab>
          <Tab value="observability" icon={<OptionsRegular />}>
            Configuration
          </Tab>
          {showFreeGrants && (
            <Tab value={SETTINGS_TAB.freeGrants} icon={<GiftRegular />}>
              Free grants
            </Tab>
          )}
        </TabList>
        <div className={styles.panel}>
          {selectedTab === 'permissions' && (
            <>
              {accessLoading || isAdminAccessLoading ? (
                <div className={styles.loadingContainer}>
                  <Spinner size="medium" label="Loading permissions..." />
                </div>
              ) : isError || !currentUserAccess ? (
                <Text className={styles.errorText}>
                  Failed to load permissions. Please try again.
                </Text>
              ) : (
                <Permissions
                  supportedPermissions={PROJECT_PERMISSIONS}
                  resourceDetails={resourceDetails}
                  myAccess={currentUserAccess}
                  checkAccess={checkAccess}
                  canEdit={userCanEdit}
                />
              )}
            </>
          )}
          {selectedTab === 'observability' && projectName && (
            accessLoading || isAdminAccessLoading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" label="Loading observability settings..." />
              </div>
            ) : isError || !currentUserAccess ? (
              <Text className={styles.errorText}>
                Failed to load Application Insights configuration. Please try again.
              </Text>
            ) : (
              <ObservabilityTab projectName={projectName} canEdit={userCanEdit} />
            )
          )}
          {selectedTab === SETTINGS_TAB.freeGrants && showFreeGrants && (
            <FreeGrantBalances project={project} />
          )}
        </div>
      </PageSection>
    </div>
  );
};
